// API Key Authentication utilities
import config from '../config/environment';

/**
 * Validate API key with the backend
 * @param {string} apiKey - The API key to validate
 * @returns {Promise<Object>} Validation response
 */
export const validateApiKey = async (apiKey) => {
  try {
    const response = await fetch(`${config.API_BASE_URL}/user/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ api_key: apiKey }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error validating API key:', error);
    return {
      api_key: '',
      first_name: '',
      last_name: '',
      email: '',
      is_success: false,
      error: error.message || 'Failed to validate API key'
    };
  }
};

/**
 * Store validated user data in localStorage
 * @param {Object} validationResponse - Response from /user/validate API
 */
export const storeUserData = (validationResponse) => {
  try {
    if (!validationResponse.is_success) {
      console.error('Cannot store invalid user data');
      return false;
    }

    // Store individual fields
    localStorage.setItem('api_key', validationResponse.api_key);
    localStorage.setItem('first_name', validationResponse.first_name);
    localStorage.setItem('last_name', validationResponse.last_name);
    localStorage.setItem('email', validationResponse.email);

    // Store complete user data object for UserContext
    const userData = {
      name: `${validationResponse.first_name} ${validationResponse.last_name}`.trim() || validationResponse.email.split('@')[0],
      email: validationResponse.email,
      first_name: validationResponse.first_name,
      last_name: validationResponse.last_name,
      api_key: validationResponse.api_key,
      organization: "Fouray's organization", // Default value
      plan: 'Community Plan' // Default value
    };

    localStorage.setItem('user-data', JSON.stringify(userData));

    console.log('User data stored successfully');
    return true;
  } catch (error) {
    console.error('Error storing user data:', error);
    return false;
  }
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = () => {
  try {
    localStorage.removeItem('api_key');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('email');
    localStorage.removeItem('user-data');

    // Remove old auth items if they exist
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('refreshToken');

    console.log('Auth data cleared');
    return true;
  } catch (error) {
    console.error('Error clearing auth data:', error);
    return false;
  }
};

/**
 * sessionStorage key holding the reason for the most recent forced logout.
 * The Login screen reads and clears this to show the user why they were signed out
 * (deactivated account, deactivated company, or an expired/invalid key).
 */
export const AUTH_LOGOUT_REASON_KEY = 'auth_logout_reason';

/**
 * Read and clear the pending forced-logout reason (if any).
 * @returns {string|null}
 */
export const consumeLogoutReason = () => {
  try {
    const reason = sessionStorage.getItem(AUTH_LOGOUT_REASON_KEY);
    if (reason) sessionStorage.removeItem(AUTH_LOGOUT_REASON_KEY);
    return reason || null;
  } catch (error) {
    return null;
  }
};

/**
 * Force a logout and redirect to the login page, optionally recording a reason
 * that the login screen will surface. Used by the global 401/403 handler when the
 * key is invalid or the account/company has been deactivated (spec §2.1).
 * @param {string} [reason]
 */
export const forceLogout = (reason) => {
  clearAuthData();
  try {
    if (reason) sessionStorage.setItem(AUTH_LOGOUT_REASON_KEY, reason);
  } catch (error) {
    // sessionStorage may be unavailable — proceed with the redirect regardless.
  }
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

/**
 * Get stored API key from localStorage
 * @returns {string|null} The stored API key or null
 */
export const getStoredApiKey = () => {
  try {
    return localStorage.getItem('api_key');
  } catch (error) {
    console.error('Error getting stored API key:', error);
    return null;
  }
};

/**
 * Get stored user data from localStorage
 * @returns {Object|null} The stored user data or null
 */
export const getStoredUserData = () => {
  try {
    const userData = localStorage.getItem('user-data');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting stored user data:', error);
    return null;
  }
};

/**
 * Check if user is authenticated (has valid API key stored)
 * @returns {boolean} True if user appears to be authenticated
 */
export const isAuthenticated = () => {
  const apiKey = getStoredApiKey();
  return !!apiKey;
};
